// Instagram API Service for Benipal Toys
// Updated to use Instagram Graph API (Instagram Basic Display API was discontinued Dec 4, 2024)
// Reference: https://developers.facebook.com/blog/post/2024/09/04/update-on-instagram-basic-display-api/

export interface InstagramPost {
  id: string;
  caption: string;
  mediaType: "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM";
  mediaUrl: string;
  permalink: string;
  timestamp: string;
  likeCount: number;
  commentsCount: number;
}

export interface InstagramStory {
  id: string;
  mediaType: "IMAGE" | "VIDEO";
  mediaUrl: string;
  permalink: string;
  timestamp: string;
}

export interface InstagramAccountInfo {
  id: string;
  username: string;
  name: string;
  profilePictureUrl: string;
  followersCount: number;
  mediaCount: number;
}

interface FacebookMediaItem {
  id: string;
  caption?: string;
  media_type: "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM";
  media_url: string;
  permalink: string;
  timestamp: string;
}

interface FacebookInsightMetric {
  name: string;
  values: Array<{ value: number }>;
}

interface FacebookStoryItem {
  id: string;
  media_type: "IMAGE" | "VIDEO";
  media_url: string;
  permalink: string;
  timestamp: string;
}

interface EmbeddedPost {
  id: string;
  embedUrl: string;
  permalink: string;
}

class InstagramService {
  private accessToken: string;
  private instagramAccountId: string;

  constructor() {
    // Instagram Graph API requires a Facebook Page connected to Instagram Business account
    // You'll need to get an Instagram Graph API access token
    this.accessToken = import.meta.env.VITE_INSTAGRAM_GRAPH_ACCESS_TOKEN || "";
    this.instagramAccountId = import.meta.env.VITE_INSTAGRAM_ACCOUNT_ID || "";
  }

  async getAccountInfo(): Promise<InstagramAccountInfo | null> {
    if (!this.accessToken || !this.instagramAccountId) {
      console.warn(
        "Instagram Graph API credentials not provided, using mock data"
      );
      return this.getMockAccountInfo();
    }

    try {
      const response = await fetch(
        `https://graph.facebook.com/v18.0/${this.instagramAccountId}?fields=id,username,name,profile_picture_url,followers_count,media_count&access_token=${this.accessToken}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Instagram account info");
      }

      const data = await response.json();

      return {
        id: data.id,
        username: data.username,
        name: data.name,
        profilePictureUrl: data.profile_picture_url,
        followersCount: data.followers_count || 0,
        mediaCount: data.media_count || 0,
      };
    } catch (error) {
      console.error("Error fetching Instagram account info:", error);
      return this.getMockAccountInfo();
    }
  }

  async getPosts(limit: number = 12): Promise<InstagramPost[]> {
    if (!this.accessToken || !this.instagramAccountId) {
      console.warn(
        "Instagram Graph API credentials not provided, using mock data"
      );
      return this.getMockPosts();
    }

    try {
      const response = await fetch(
        `https://graph.facebook.com/v18.0/${this.instagramAccountId}/media?fields=id,caption,media_type,media_url,permalink,timestamp&limit=${limit}&access_token=${this.accessToken}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Instagram posts");
      }

      const data = await response.json() as { data: FacebookMediaItem[] };

      // Get insights for each post (likes, comments)
      const postsWithInsights = await Promise.all(
        data.data.map(async (post) => {
          try {
            const insightsResponse = await fetch(
              `https://graph.facebook.com/v18.0/${post.id}/insights?metric=likes,comments&access_token=${this.accessToken}`
            );

            if (insightsResponse.ok) {
              const insightsData = await insightsResponse.json() as { data: FacebookInsightMetric[] };
              const likes =
                insightsData.data.find((metric) => metric.name === "likes")
                  ?.values[0]?.value || 0;
              const comments =
                insightsData.data.find(
                  (metric) => metric.name === "comments"
                )?.values[0]?.value || 0;

              return {
                id: post.id,
                caption: post.caption || "",
                mediaType: post.media_type,
                mediaUrl: post.media_url,
                permalink: post.permalink,
                timestamp: post.timestamp,
                likeCount: likes,
                commentsCount: comments,
              };
            }
          } catch (_error) {
            console.warn("Failed to fetch insights for post:", post.id);
          }

          return {
            id: post.id,
            caption: post.caption || "",
            mediaType: post.media_type,
            mediaUrl: post.media_url,
            permalink: post.permalink,
            timestamp: post.timestamp,
            likeCount: 0,
            commentsCount: 0,
          };
        })
      );

      return postsWithInsights;
    } catch (error) {
      console.error("Error fetching Instagram posts:", error);
      return this.getMockPosts();
    }
  }

  async getStories(): Promise<InstagramStory[]> {
    if (!this.accessToken || !this.instagramAccountId) {
      console.warn(
        "Instagram Graph API credentials not provided, using mock data"
      );
      return this.getMockStories();
    }

    try {
      const response = await fetch(
        `https://graph.facebook.com/v18.0/${this.instagramAccountId}/stories?fields=id,media_type,media_url,permalink,timestamp&access_token=${this.accessToken}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Instagram stories");
      }

      const data = await response.json() as { data: FacebookStoryItem[] };

      return data.data.map((story) => ({
        id: story.id,
        mediaType: story.media_type,
        mediaUrl: story.media_url,
        permalink: story.permalink,
        timestamp: story.timestamp,
      }));
    } catch (error) {
      console.error("Error fetching Instagram stories:", error);
      return this.getMockStories();
    }
  }

  // Alternative: Instagram Embed API for public posts
  async getEmbeddedPosts(postUrls: string[]): Promise<EmbeddedPost[]> {
    // This method can be used to embed Instagram posts without API access
    // Requires post URLs to be publicly accessible
    return postUrls.map((url, index) => ({
      id: `embed_${index}`,
      embedUrl: `https://www.instagram.com/p/${url.split("/").pop()}/embed/`,
      permalink: url,
    }));
  }

  // Public method to get mock account info
  getMockAccountInfo(): InstagramAccountInfo {
    return {
      id: "mock_instagram_id",
      username: "benipal_toys",
      name: "Benipal Toys & Gift Store",
      profilePictureUrl:
        "https://via.placeholder.com/150x150/ec4899/ffffff?text=BT",
      followersCount: 1183,
      mediaCount: 297,
    };
  }

  private getMockPosts(): InstagramPost[] {
    return [
      {
        id: "mock_post_1",
        caption:
          "Check out our amazing new toy collection! 🧸✨ Perfect for sparking imagination and creativity in kids.",
        mediaType: "IMAGE",
        mediaUrl:
          "https://via.placeholder.com/400x400/ec4899/ffffff?text=Toy+Collection",
        permalink: "https://www.instagram.com/p/mock1/",
        timestamp: "2024-01-15T10:00:00Z",
        likeCount: 45,
        commentsCount: 8,
      },
      {
        id: "mock_post_2",
        caption:
          "Educational toys that make learning fun! 🧠🎯 Our STEM collection helps kids develop critical thinking skills.",
        mediaType: "IMAGE",
        mediaUrl:
          "https://via.placeholder.com/400x400/a855f7/ffffff?text=Educational",
        permalink: "https://www.instagram.com/p/mock2/",
        timestamp: "2024-01-12T14:30:00Z",
        likeCount: 32,
        commentsCount: 5,
      },
      {
        id: "mock_post_3",
        caption:
          "Action figures unboxing! 🦸‍♂️ Kids love these detailed characters for imaginative play.",
        mediaType: "VIDEO",
        mediaUrl:
          "https://via.placeholder.com/400x400/3b82f6/ffffff?text=Action+Figures",
        permalink: "https://www.instagram.com/p/mock3/",
        timestamp: "2024-01-10T16:20:00Z",
        likeCount: 67,
        commentsCount: 12,
      },
    ];
  }

  private getMockStories(): InstagramStory[] {
    return [
      {
        id: "mock_story_1",
        mediaType: "IMAGE",
        mediaUrl:
          "https://via.placeholder.com/400x600/ec4899/ffffff?text=Story+1",
        permalink: "https://www.instagram.com/stories/mock1/",
        timestamp: "2024-01-15T09:00:00Z",
      },
      {
        id: "mock_story_2",
        mediaType: "VIDEO",
        mediaUrl:
          "https://via.placeholder.com/400x600/a855f7/ffffff?text=Story+2",
        permalink: "https://www.instagram.com/stories/mock2/",
        timestamp: "2024-01-15T08:30:00Z",
      },
    ];
  }
}

export const instagramService = new InstagramService();
